import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { appConfirmMessage } from '../../../utils/appConfirm';
import {
  FaSearch,
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaFileImport,
  FaFileWord,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import { useAdminListOverlay } from '../../../utils/useAdminPageSearch';
import SupplierForm from './SupplierForm';
import SupplierDetailModal from './SupplierDetailModal';
import BulkAddSuppliersModal from './BulkAddSuppliersModal';
import SupplierCatalogModal from './SupplierCatalogModal';
import '../styles/InventoryProcurement.css';

const FIND = `
  query FindAllSuppliers {
    findAllSuppliers {
      id code name tradingName abn contactPerson phone email status
      address paymentTerms notes productCount lastOrderDate
    }
  }
`;

const REMOVE = `
  mutation RemoveSupplier($id: String!) {
    removeSupplier(id: $id)
  }
`;

const PAGE_SIZE = 8;

function statusLabel(status) {
  const s = String(status || '');
  if (s === 'OnHold' || s === 'On Hold') return 'On Hold';
  return s || '—';
}

function statusClass(status) {
  const s = String(status || '').toLowerCase();
  if (s.includes('hold')) return 'iproc-pill--onhold';
  if (s.includes('inactive')) return 'iproc-pill--inactive';
  return 'iproc-pill--active';
}

function formatDate(raw) {
  if (!raw) return '—';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export default function SuppliersDashboard() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('All');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [catalogFor, setCatalogFor] = useState(null);
  const [catalogPick, setCatalogPick] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const overlay = useAdminListOverlay(rows);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await graphqlRequest(FIND);
      setRows(Array.isArray(data?.findAllSuppliers) ? data.findAllSuppliers : []);
    } catch (e) {
      toast.error(e?.message || 'Failed to load suppliers');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (status !== 'All' && r.status !== status) return false;
      if (!q) return true;
      return `${r.code} ${r.name} ${r.contactPerson} ${r.email} ${r.phone}`
        .toLowerCase()
        .includes(q);
    });
  }, [rows, search, status]);

  useEffect(() => setPage(1), [search, status]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);
  const pageRows = filtered.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE);

  const handleDelete = async (row) => {
    if (!await appConfirmMessage(`Delete supplier "${row.name}"?`)) return;
    try {
      await graphqlRequest(REMOVE, { id: row.id });
      toast.success('Supplier deleted');
      await load();
    } catch (e) {
      toast.error(e?.message || 'Failed to delete');
    }
  };

  return (
    <div className="iproc-page">
      <header className="iproc-header">
        <h1>Suppliers</h1>
        <div className="iproc-header-actions">
          <button
            type="button"
            className="iproc-btn"
            onClick={() => {
              setCatalogFor(null);
              setCatalogPick(true);
            }}
          >
            <FaFileWord /> Upload Word catalog
          </button>
          <button
            type="button"
            className="iproc-btn"
            onClick={() => setShowBulk(true)}
          >
            <FaPlus /> Bulk Add
          </button>
          <button
            type="button"
            className="iproc-btn iproc-btn--primary"
            onClick={() => overlay.openAdd()}
          >
            <FaPlus /> Add Supplier
          </button>
        </div>
      </header>

      <div className="iproc-panel">
        <div className="iproc-toolbar">
          <label className="iproc-search">
            <FaSearch />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search suppliers..."
            />
          </label>
          <select
            className="iproc-select"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="All">All statuses</option>
            <option value="Active">Active</option>
            <option value="OnHold">On Hold</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>

        <div className="iproc-table-wrap">
          <table className="iproc-table">
            <thead>
              <tr>
                <th>Supplier ID</th>
                <th>Name</th>
                <th>Contact Person</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Products</th>
                <th>Last Order</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="9" className="iproc-empty">Loading suppliers…</td></tr>
              ) : pageRows.length === 0 ? (
                <tr><td colSpan="9" className="iproc-empty">No suppliers yet.</td></tr>
              ) : (
                pageRows.map((row) => (
                  <tr key={row.id}>
                    <td className="iproc-code">{row.code}</td>
                    <td className="iproc-name">{row.name}</td>
                    <td>{row.contactPerson || '—'}</td>
                    <td>{row.phone || '—'}</td>
                    <td>{row.email || '—'}</td>
                    <td>{row.productCount ?? 0}</td>
                    <td>{formatDate(row.lastOrderDate)}</td>
                    <td>
                      <span className={`iproc-pill ${statusClass(row.status)}`}>
                        {statusLabel(row.status)}
                      </span>
                    </td>
                    <td>
                      <div className="iproc-actions-cell">
                        <button
                          type="button"
                          className="iproc-icon-btn"
                          title="Upload Word catalog"
                          onClick={() => setCatalogFor(row)}
                        >
                          <FaFileImport />
                        </button>
                        <button type="button" className="iproc-icon-btn" title="View" onClick={() => overlay.openView(row)}>
                          <FaEye />
                        </button>
                        <button
                          type="button"
                          className="iproc-icon-btn"
                          title="Edit"
                          onClick={() => overlay.openEdit(row)}
                        >
                          <FaEdit />
                        </button>
                        <button type="button" className="iproc-icon-btn danger" title="Delete" onClick={() => void handleDelete(row)}>
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="iproc-foot">
          <span>Showing {pageRows.length} of {filtered.length} suppliers</span>
          <div className="iproc-pages">
            {Array.from({ length: totalPages }, (_, i) => i + 1).slice(0, 8).map((n) => (
              <button
                key={n}
                type="button"
                className={`iproc-page-btn ${pageSafe === n ? 'is-active' : ''}`}
                onClick={() => setPage(n)}
              >
                {n}
              </button>
            ))}
          </div>
        </div>
      </div>

      {overlay.showForm ? (
        <SupplierForm
          editData={overlay.editing}
          onClose={overlay.closeOverlay}
          onSaved={() => {
            overlay.closeOverlay();
            void load();
          }}
        />
      ) : null}

      {showBulk ? (
        <BulkAddSuppliersModal
          onClose={() => setShowBulk(false)}
          onSaved={() => {
            setShowBulk(false);
            void load();
          }}
        />
      ) : null}

      {catalogFor || catalogPick ? (
        <SupplierCatalogModal
          supplier={catalogFor}
          suppliers={rows}
          onClose={() => {
            setCatalogFor(null);
            setCatalogPick(false);
          }}
          onSaved={() => {
            setCatalogFor(null);
            setCatalogPick(false);
            void load();
          }}
        />
      ) : null}

      {overlay.viewing ? (
        <SupplierDetailModal
          supplier={overlay.viewing}
          onClose={overlay.closeOverlay}
          onImport={(row) => {
            overlay.closeOverlay();
            setCatalogFor(row);
          }}
          onEdit={overlay.openEdit}
        />
      ) : null}
    </div>
  );
}
